"use client";
/**
 * Share bar under every blog post. Each click records one Secondary "Blog share"
 * conversion (trackingTaxonomy.ts); the network rides in metadata.location. It used to
 * write a CTA click instead, and was never rendered, so blog sharing was invisible.
 */
import React from 'react';
import { Facebook, Linkedin, Link2 } from 'lucide-react';
import { XTwitterIcon } from '@/components/ui/x-twitter-icon';
import { toast } from 'sonner';
import { cn, copyToClipboard } from '@/lib/utils';
import { trackConversion } from '@/utils/conversionTracking';

interface SocialShareProps {
  title: string;
  url: string;
  excerpt?: string;
  className?: string;
}

export function SocialShare({ title, url, className }: SocialShareProps) {
  const shareUrls = {
    twitter: `https://x.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  };

  const share = (network: string) => trackConversion('blog_share', 'Blog Share', { location: network });

  const copyLink = async () => {
    try {
      await copyToClipboard(url);
      toast.success('Link copied to clipboard');
      share('copy');
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const iconBtn = "flex items-center justify-center w-9 h-9 rounded-full border border-border bg-background hover:bg-muted hover:border-primary/30 transition-all duration-200 text-muted-foreground hover:text-foreground";

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <span className="text-sm font-medium text-muted-foreground">Share</span>
      <div className="flex items-center gap-2">
        <button
          onClick={() => { share('twitter'); window.open(shareUrls.twitter, '_blank', 'width=600,height=400'); }}
          className={iconBtn}
          title="Share on X"
          aria-label="Share on X"
        >
          <XTwitterIcon size={15} />
        </button>
        <button
          onClick={() => { share('linkedin'); window.open(shareUrls.linkedin, '_blank', 'width=600,height=400'); }}
          className={iconBtn}
          title="Share on LinkedIn"
          aria-label="Share on LinkedIn"
        >
          <Linkedin className="h-4 w-4" />
        </button>
        <button
          onClick={() => { share('facebook'); window.open(shareUrls.facebook, '_blank', 'width=600,height=400'); }}
          className={iconBtn}
          title="Share on Facebook"
          aria-label="Share on Facebook"
        >
          <Facebook className="h-4 w-4" />
        </button>
        <button
          onClick={copyLink}
          className={iconBtn}
          title="Copy link"
          aria-label="Copy link"
        >
          <Link2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
